import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface OptionButtonItem<T = string | number | null> {
	label?: string;
	labelKey?: string;
	value: T;
}

@Component({
	selector: 'app-option-button-group',
	templateUrl: './option-button-group.component.html',
	styleUrls: ['./option-button-group.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => OptionButtonGroupComponent),
			multi: true
		}
	]
})
export class OptionButtonGroupComponent<T = string | number | null> implements ControlValueAccessor {
	@Input() label?: string;
	@Input() labelKey?: string;
	@Input() anyLabelKey = 'searchProperties.filters.any';
	@Input() options: OptionButtonItem<T>[] = [];
	@Input() disabled = false;
	@Input() isColorVariant?: boolean = false;

	public value: T | null = null;

	private onChange: (value: T | null) => void = () => {};
	private onTouched: () => void = () => {};

	constructor(private readonly cdr: ChangeDetectorRef) {}

	writeValue(value: T | null): void {
		this.value = value ?? null;
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: T | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	selectOption(value: T | null): void {
		if (this.disabled) {
			return;
		}

		this.value = value;
		this.onChange(this.value);
		this.onTouched();
		this.cdr.markForCheck();
	}

	isSelected(value: T | null): boolean {
		return this.value === value;
	}

	trackByValue(_: number, option: OptionButtonItem<T>): T {
		return option.value;
	}
}
